"use strict";


function Sections() {
  this.$document = document;
  this.namespace = ".section-js-events";

  document.addEventListener("shopify:section:load", (evt) => {
    const id = evt.detail.sectionId;
    const container = evt.target.querySelector(`[data-section-id="${id}"]`);
    const type = container.getAttribute("data-section-type");

    this.load(type, container);
  });
}

Object.assign(Sections.prototype, {
  registered: {},
  instances: [],
  extensions: { "*": [] },

  register(type, properties) {
    function Section(data) {
      this.type = type;
      Master.call(this, data);
    }

    Section.prototype = Object.create(Master.prototype);
    Section.prototype.constructor = Section;
    Object.assign(Section.prototype, properties);

    this.registered[type] = Section;
  },

  load(types, containers) {
    types = this._normalizeTypeParam(types);
    containers = this._normalizeContainersParam(containers);

    types.forEach((type) => {
      const Section = this.registered[type];
      let selection = containers;

      if (typeof Section === "undefined") return;

      if (typeof selection === "undefined") {
        selection = document.querySelectorAll(`[data-section-type="${type}"]`);
      }

      selection = Array.from(selection);

      selection.forEach((container) => {
        if (this._instanceExists(container)) return;

        const extensions = this.extensions["*"].concat(this.extensions[type] || []);
        const instance = new Section({
          container: container,
          extensions: extensions,
          id: container.getAttribute("data-section-id"),
        });

        instance.trigger("section_load");
        this.instances.push(instance);
      });
    });
  },

  extend(types, extension) {
    types = this._normalizeTypeParam(types);

    types.forEach((type) => {
      this.extensions[type] = this.extensions[type] || [];
      this.extensions[type].push(extension);

      if (typeof this.registered[type] === "undefined") {
        return;
      }

      this.instances.forEach((instance) => {
        if (instance.type !== type) {
          return;
        }
        instance.extend(extension);
      });
    });
  },

  isInstance(type) {
    return typeof this.instances.find(inst => inst.type === type) === "object";
  },

  getInstances(type) {
    return new Promise((resolve, reject) => {
      const instances = this.instances.filter(inst => inst.type === type);

      if (instances.length === 0) {
        reject();
      } else {
        resolve(instances);
      }
    });
  },

  on(event, handler) {
    this.$document.addEventListener(event + this.namespace, handler);
  },

  off(event, handler) {
    this.$document.removeEventListener(event + this.namespace, handler);
  },

  trigger(event, detail) {
    const evt = new CustomEvent(event, { detail });
    this.instances.forEach((instance) => {
      instance.trigger(evt);
    });
  },

  _normalizeTypeParam(types) {
    if (types === "*") {
      types = Object.keys(this.registered);
    } else if (typeof types === "string") {
      types = [types];
    }

    return types.map((type) => type.toLowerCase());
  },

  _normalizeContainersParam(containers) {
    if (!Array.isArray(containers) && typeof containers === "object") {
      containers = [containers];
    }
    return containers;
  },

  _instanceExists(container) {
    return typeof this.instances.find(inst => inst.id === container.getAttribute("data-section-id")) !== "undefined";
  },
});

/**
 * Master section class that all sections inherit from
 * @constructor
 */

function Master(data) {
  this.container = data.container;
  this.id = data.id;
  this.namespace = "." + data.id;
  this.extensions = data.extensions || [];
  this.$eventBinder = this.container;

  _applyExtensions.call(this);
  _applyEditorHandlers.call(this);
  _applyDefaultHandlers.call(this);
}

Master.prototype = {
  onLoad() {},
  onUnload() {},
  onSelect() {},
  onDeselect() {},
  onBlockSelect() {},
  onBlockDeselect() {},

  on(event, handler) {
    this.$eventBinder.addEventListener(event + this.namespace, handler);
  },

  one(event, handler) {
    const onceHandler = (e) => {
      handler(e);
      this.$eventBinder.removeEventListener(event + this.namespace, onceHandler);
    };
    this.$eventBinder.addEventListener(event + this.namespace, onceHandler);
  },

  off(event, handler) {
    this.$eventBinder.removeEventListener(event + this.namespace, handler);
  },

  trigger(event, detail = {}) {
    const evt = new CustomEvent(event + this.namespace, { detail });
    this.$eventBinder.dispatchEvent(evt);
  },

  extend(extension) {
    const { init, ...rest } = extension;
    this.extensions.push(extension);

    Object.assign(this, rest);

    if (typeof init === "function") {
      init.apply(this);
    }
  },
};

function _applyExtensions() {
  this.extensions.forEach((extension) => {
    this.extend(extension);
  });
}

function _applyEditorHandlers() {
  document.addEventListener("shopify:section:unload" + this.namespace, _onSectionUnload.bind(this));
  document.addEventListener("shopify:section:select" + this.namespace, _onSelect.bind(this));
  document.addEventListener("shopify:section:deselect" + this.namespace, _onDeselect.bind(this));
  document.addEventListener("shopify:block:select" + this.namespace, _onBlockSelect.bind(this));
  document.addEventListener("shopify:block:deselect" + this.namespace, _onBlockDeselect.bind(this));
}

function _applyDefaultHandlers() {
  this.on("section_load", this.onLoad.bind(this));
  this.on("section_unload", this.onUnload.bind(this));
  this.on("section_select", this.onSelect.bind(this));
  this.on("section_deselect", this.onDeselect.bind(this));
  this.on("block_select", this.onBlockSelect.bind(this));
  this.on("block_deselect", this.onBlockDeselect.bind(this));
}

function _onSectionUnload(event) {
  if (this.id !== event.detail.sectionId) {
    return;
  }

  event.type = "section_unload";
  this.trigger(event);

  this.off(this.namespace);
  window.__sections__.off(this.namespace);

  const idx = window.__sections__.instances.findIndex(inst => inst.id === this.id);
  if (idx !== -1) window.__sections__.instances.splice(idx, 1);
}

function _onSelect(event) {
  if (this.id !== event.detail.sectionId) {
    return;
  }

  event.type = "section_select";
  this.trigger(event);
}

function _onDeselect(event) {
  if (this.id !== event.detail.sectionId) {
    return;
  }

  event.type = "section_deselect";
  this.trigger(event);
}

function _onBlockSelect(event) {
  if (this.id !== event.detail.sectionId) {
    return;
  }

  event.type = "block_select";
  this.trigger(event);
}

function _onBlockDeselect(event) {
  if (this.id !== event.detail.sectionId) {
    return;
  }

  event.type = "block_deselect";
  this.trigger(event);
}

window.__sections__ = window.__sections__ || new Sections();
export default window.__sections__;
